import * as React from 'react';
import type { CustomerRecord } from '../services/customersApi';
import { customersApi } from '../services/customersApi';

export function useCustomers(projectUuid: string | undefined) {
  const [customers, setCustomers] = React.useState<CustomerRecord[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  const reload = React.useCallback(() => {
    setLoading(true);
    setError(null);
    customersApi
      .listCustomers(projectUuid)
      .then(({ customers: data }) => setCustomers(data))
      .catch(() => setError('Failed to load customers. Please try again.'))
      .finally(() => setLoading(false));
  }, [projectUuid]);

  React.useEffect(() => {
    reload();
  }, [reload]);

  const deleteCustomer = React.useCallback(
    async (customerUuid: string) => {
      // Optimistically remove from the list; restore the snapshot if the request fails.
      let snapshot: CustomerRecord[] | undefined;
      setCustomers((prev) => {
        snapshot = prev;
        return prev.filter((customer) => customer.uuid !== customerUuid);
      });
      try {
        await customersApi.deleteCustomer(projectUuid, customerUuid);
      } catch (err) {
        if (snapshot !== undefined) setCustomers(snapshot);
        throw err;
      }
    },
    [projectUuid]
  );

  return { customers, loading, error, reload, deleteCustomer };
}
